import React, { useEffect, useState } from 'react';
import { 
  useGlobalActiveGeography, 
  useGlobalSelectedId,
  onSetActiveGeography,
  onSetSelectedId,
  onSetActivePage,
  onSetDBModuleId,
  onSetModuleId,
  onSetActiveIndicator,
  onSetBivariateIndicator,
  onSetActivePointStatus,
} from '../../data/StatusStore';
import { processSparklineTags } from './InlineSparkline';
import './WideBlurb.scss'; // Reuse the same SCSS as WideBlurb

export interface ImageBlurbProps {
  options: any;
  data: any;
  width: number;
  height: number;
  colorClass: string;
  setShowBackToStories: Function | null;
}

/**
 * Recursively parses custom markup.
 * 
 * Supported tags (case-insensitive) include:
 *   H1, red, teal, green, b, p
 * 
 * The syntax supports two formats:
 *   - Full form: [tag] ... [/tag]
 *   - Shorthand (toggle) form: [tag/] ... [tag/]
 */
const parseCustomMarkup = (input: string): string => {
  let pos = 0;

  function parseUntil(stopTag?: string): string {
    let result = '';

    while (pos < input.length) {
      if (input[pos] === '[') {
        const end = input.indexOf(']', pos);
        if (end === -1) {
          result += input.slice(pos);
          pos = input.length;
          break;
        }
        let token = input.slice(pos + 1, end).trim();
        if (stopTag) {
          const lowerToken = token.toLowerCase();
          if (
            lowerToken === '/' + stopTag.toLowerCase() ||
            lowerToken === stopTag.toLowerCase() + '/'
          ) {
            pos = end + 1;
            return result;
          }
        }
        pos = end + 1;
        if (token.endsWith('/')) {
          token = token.slice(0, -1).trim();
        }
        const tagName = token;
        const inner = parseUntil(tagName);
        result += `<${tagName.toLowerCase()} class="custom-${tagName.toLowerCase()}">` + inner + `</${tagName.toLowerCase()}>`;
      } else {
        result += input[pos];
        pos++;
      }
    }
    return result;
  }

  return `<span class="custom-text-plain">` + parseUntil() + `</span>`;
};

const ImageBlurb: React.FC<ImageBlurbProps> = ({ options, data, width, height, colorClass, setShowBackToStories }) => {
  // --- TEXT LOGIC (like WideBlurb) ---
  const contentTemplate = options.variables[0].sort["Stories Content"] || "";
  const variableName = options.variables[0].variable;
  const activeGeography = useGlobalActiveGeography();
  const selectedId = useGlobalSelectedId();
  const [htmlContent, setHtmlContent] = useState<string>(() => parseCustomMarkup(contentTemplate));

  // --- IMAGE LOGIC (like Image) ---
  const fileName = options.variables[0].sort["Stories Asset"];
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  // Helper to build the dynamic path segment based on geography + selectedId
  const buildDynamicPath = (geo: string, id: string): string => {
    let prefix = "";
    let suffix = id;
    const geoDir = geo === 'Tract' ? 'NTA' : geo;
    if (geo === 'NTA' || geo === 'Tract') {
      prefix = id ? id.slice(0, 2).toLowerCase() : '';
      suffix = id ? id.slice(-4) : '';
    }
    if (geo === 'CD') {
      suffix = `cd${id}`;
    }
    if (geo === 'NTA' || geo === 'Tract') {
      return `${geoDir}/${prefix}/${prefix}${suffix}`;
    }
    return `${geoDir}/${suffix}`;
  };

  // Compute htmlContent and imageSrc whenever selectedId changes
  const updateContentAndImage = () => {
    if (selectedId) {
      const dynamicPath = buildDynamicPath(activeGeography, selectedId);
      const nextText = contentTemplate.replace("{}", dynamicPath);
      setHtmlContent(processSparklineTags(nextText, parseCustomMarkup, data?.dataArray?.[variableName], colorClass, variableName));

      if (fileName) {
        const finalFileName = fileName.replace("{}", dynamicPath);
        try {
          const resolved = require(`../../${finalFileName}`);
          setImageSrc(resolved);
        } catch (err) {
          console.error("Image not found:", finalFileName, err);
          setImageSrc(null);
        }
      } else {
        setImageSrc(null);
      }
    } else {
      // No selection: fall back to template as-is and clear image
      setHtmlContent(processSparklineTags(contentTemplate, parseCustomMarkup, data?.dataArray?.[variableName], colorClass, variableName));
      setImageSrc(null);
    }
  };

  useEffect(() => {
    console.log("B010726 selectedId", selectedId);
    console.log("B010726 activeGeography", activeGeography);
    updateContentAndImage();
    // Only re-run when selectedId changes, per request
  }, [activeGeography, selectedId]);

  useEffect(() => {
    console.log("B010726 LOADED", selectedId);
    // If selectedId is initially null, try to read it from URL params
    if (!selectedId) {
      const params = new URLSearchParams(window.location.search);
      const urlSelectedId = params.get('selectedId');
      if (urlSelectedId) {
        onSetSelectedId(urlSelectedId);
      }
    }
    updateContentAndImage();
    // Only re-run when selectedId changes, per request
  }, []);

  
  // --- TOOLTIP & CLICK LOGIC ---
  const [showTooltip, setShowTooltip] = useState(false);

  // This function retrieves the Stories Click and calls implementDirective.
  const handleImageClick = () => {
    const storiesDirective = options.variables.length > 1
      ? options.variables
          .map((variable: any) => variable.sort["Stories Click"])
          .filter((directive: string | null) => directive && directive.trim() !== "")
          .join(",")
      : options.variables[0]?.sort["Stories Click"] || "";
    if (storiesDirective) {
      implementDirective(storiesDirective);
    }
  };

  const implementDirective = (directive: string) => {
    console.log("EE033125 Implementing directive:", directive);
    const directives = directive.split(','); // Split the directive on commas
    directives.forEach((dir) => {
      const [key, value] = dir.split('='); // Split each directive on '='
      if (key && value) {
        console.log(`EE033125 Key: ${key.trim()}, Value: ${value.trim()}`);
        switch (key.trim()) {
          case "moduleId":
            onSetModuleId(value.trim());
            break;
          case "DBModuleId":
            onSetDBModuleId(value.trim());
            break;
          case "activeGeography":
            onSetActiveGeography(value.trim());
            break;
          case "selectedId":
            console.log(`EE033125 Setting selectedId: ${value.trim()}`);
            // onSetSelectedId is assumed available from StatusStore
            console.log("A040525 value.trim()", value.trim());
            onSetSelectedId(value.trim());
            break;
          case "activeIndicator":
            console.log(`EE033125 Setting selectedId: ${value.trim()}`);
            // onSetSelectedId is assumed available from StatusStore
            onSetActiveIndicator(value.trim(), options.type.toLowerCase(), options.variables[0].sort.moduleId);
            break;
          case "bivariateIndicator":
            console.log(`EE033125 Setting selectedId: ${value.trim()}`);
            // onSetSelectedId is assumed available from StatusStore
            onSetBivariateIndicator(value.trim(), options.type.toLowerCase(), options.variables[0].sort.moduleId);
            break;
          case "page":
            console.log(`EE033125 Setting page: ${value.trim()}`);
            // DO NOTHING, PAGE MUST COME LAST! onSetActivePage(value.trim());
            break;
          case "activePointStatus":
            console.log(`EE033125 Setting page: ${value.trim()}`);
            onSetActivePointStatus(value.trim());
            break;
          default:
            console.warn(`Unknown directive key: ${key.trim()}`);
        }
      }
    });
    
    setTimeout(() => {
      directives.forEach((dir) => {
      const [key, value] = dir.split('='); // Split each directive on '='
      if (key && value) {
        console.log(`EE033125 Key: ${key.trim()}, Value: ${value.trim()}`);
        switch (key.trim()) {
        case "page":
          console.log(`EE033125 Setting page: ${value.trim()}`);
          setShowBackToStories && setShowBackToStories(true);
          onSetActivePage(value.trim());
          break;
        default:
          // DO NOTHING, THESE SHOULD BE HANDLED WITH THE ABOVE SWITCH
          console.warn(`Unknown directive key: ${key.trim()}`);
        }
      }
      });
    }, 250);
  };
  console.log("B010726 imageSrc", imageSrc)
  return (
    <div 
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',  // Text on top, image below
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* TEXT SECTION */}
      <div
        style={{
          width: '100%',
          lineHeight: '12pt',
          overflowY: 'auto',
          padding: '16px 0',
          maxWidth: '600px',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>

      {/* IMAGE SECTION */}
      {imageSrc && (
        <div 
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            overflow: 'hidden',
            position: 'relative'
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={handleImageClick}
        >
          <img
            src={imageSrc}
            alt="Story Asset"
            style={{
              width: '950px',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: '550px',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
          />
          {/* Tooltip overlay */}
          {showTooltip && (
            <div className="image-tooltip">
              Click to visit
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageBlurb;
